// Per-page renderer for "p2p-analytics" — extracted from reportBuilder.js.
// Signature: async (api, doc, ctx) => { y, aiInsights, aiRecommendations }
import autoTable from 'jspdf-autotable';
import { NAVY, formatValue, fetchPageAI } from '../utils';
import { drawVerticalBarChart } from '../charts';

export async function renderP2P(api, doc, ctx) {
  const { qs, filterDesc, cs, pw, page, filters } = ctx;
  let y = ctx.y;
  let aiInsights = [];
  let aiRecommendations = [];

  const res = await api.get(`/analytics/p2p?${qs}`);
  const d = res.data;
  const kpis = d.kpis || {};
  const invKpis = d.invoice_kpis || {};
  const charts = d.charts || {};

  const dataContext = `Total Transactions: ${kpis.total_transactions}\nTotal Spend: ${cs}${kpis.total_spend?.toLocaleString()}\nUnique POs: ${kpis.unique_pos}\nUnique PRs: ${kpis.unique_prs}\nPO Coverage (% of transactions): ${kpis.po_coverage_pct}%\nPR Coverage (% of transactions): ${kpis.pr_coverage_pct}%\nContracted (% of spend): ${kpis.contracted_pct}%\nMaverick composite (% of transactions): ${kpis.maverick_pct}% (${cs}${kpis.maverick_spend?.toLocaleString()})\nOff-contract (% of spend): ${kpis.off_contract_pct}%\nNon-PO (% of transactions): ${kpis.non_po_pct}%\n2-Way Match (% of transactions): ${invKpis.two_way_match_rate}%\n3-Way Match (% of transactions): ${invKpis.three_way_match_rate}%\nGRN Coverage (% of transactions): ${invKpis.grn_coverage_pct}%\nAvg PR-to-PO: ${invKpis.avg_pr_to_po_days ?? 'N/A'} days\nAvg PO-to-GRN: ${invKpis.avg_po_to_grn_days ?? 'N/A'} days\nAvg Invoice Age: ${invKpis.avg_invoice_age_days ?? 'N/A'} days\nBuyers: ${kpis.unique_buyers}`;

  try {
    const aiRes = await fetchPageAI(api, { page, dataContext, filterDesc, filters });
    aiInsights = aiRes.insights;
    aiRecommendations = aiRes.recommendations;
  } catch {}

  autoTable(doc, {
    startY: y,
    head: [['POs', 'PRs', 'PO Coverage (% of txns)', 'Avg PO Value', 'PR-to-PO (% of PRs)', 'Buyers']],
    body: [[
      String(kpis.unique_pos || 0), String(kpis.unique_prs || 0),
      `${kpis.po_coverage_pct || 0}%`, formatValue(kpis.avg_po_value || 0),
      `${kpis.pr_to_po_rate || 0}%`, String(kpis.unique_buyers || 0),
    ]],
    styles: { fontSize: 8, halign: 'center' },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 6;

  autoTable(doc, {
    startY: y,
    head: [['Invoices', 'Avg Invoice', '2-Way Match (% of txns)', '3-Way Match (% of txns)', 'GRN Coverage (% of txns)', 'Contracted (% of spend)']],
    body: [[
      String(invKpis.unique_invoices || 0), formatValue(invKpis.avg_invoice_value || 0),
      `${invKpis.two_way_match_rate || 0}%`, `${invKpis.three_way_match_rate || 0}%`,
      `${invKpis.grn_coverage_pct || 0}%`, `${kpis.contracted_pct || 0}%`,
    ]],
    styles: { fontSize: 8, halign: 'center' },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 6;

  autoTable(doc, {
    startY: y,
    head: [['Cycle Metric', 'Average Days']],
    body: [
      ['PR to PO', invKpis.avg_pr_to_po_days != null ? `${invKpis.avg_pr_to_po_days}` : 'N/A'],
      ['PO to GRN', invKpis.avg_po_to_grn_days != null ? `${invKpis.avg_po_to_grn_days}` : 'N/A'],
      ['GRN to Invoice', invKpis.avg_grn_to_invoice_days != null ? `${invKpis.avg_grn_to_invoice_days}` : 'N/A'],
      ['PO to Invoice', invKpis.avg_po_to_invoice_days != null ? `${invKpis.avg_po_to_invoice_days}` : 'N/A'],
    ],
    styles: { fontSize: 8 }, headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  if (charts.po_by_category?.length > 0) {
    if (y > 180) { doc.addPage(); y = 15; }
    const catData = charts.po_by_category.map(c => ({ label: c.category, value: c.spend }));
    y = drawVerticalBarChart(doc, 14, y, pw - 28, 65, catData, { title: 'PO Spend by Category' });
  }

  if (charts.po_by_buyer?.length > 0) {
    if (y > 180) { doc.addPage(); y = 15; }
    const buyerData = charts.po_by_buyer.map(b => ({ label: b.buyer, value: b.spend }));
    y = drawVerticalBarChart(doc, 14, y, pw - 28, 65, buyerData, { title: 'PO Spend by Buyer' });
  }

  if (charts.invoice_aging?.length > 0) {
    if (y > 190) { doc.addPage(); y = 15; }
    const agingData = charts.invoice_aging.map(a => ({ label: a.bucket, value: a.count }));
    // Aging buckets are invoice COUNTS, not currency. Use a plain integer formatter
    // so the Y-axis and bar labels don't read "Rs 17 / Rs 127 / Rs 3K" which is nonsensical.
    y = drawVerticalBarChart(doc, 14, y, pw - 28, 55, agingData, {
      title: 'Invoice Aging Distribution (invoice counts)',
      valueFormatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(Math.round(v))),
    });
  }

  if (charts.invoice_by_supplier?.length > 0) {
    if (y > 180) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Supplier', 'Invoices', 'Spend']],
      body: charts.invoice_by_supplier.map(s => [s.supplier, String(s.invoice_count), formatValue(s.spend)]),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  if (charts.maverick_by_category?.length > 0) {
    if (y > 180) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Category', 'Maverick Spend', 'Transactions']],
      body: charts.maverick_by_category.map(m => [m.category, formatValue(m.spend), String(m.count)]),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  return { y, aiInsights, aiRecommendations };
}
